import { DIRS, hexDist, hexLen, hexToWorld, ROW, type Hex } from '../hex.ts';
import { dist2, idiv, imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';
import { MIRROR_TURN } from './litter-layout.ts';

/**
 * The cleric's last road, chapter 3 (docs/sendas/clerigo.md, «La nieve»): its grounds, the same from the seed in the
 * sim and the drawing, integers only. Up is −y (the top of the screen).
 *
 * - `colLayout` (act 1, the white col): a way three hexes wide winding up a col between drifts and rock, from its foot to
 *   the pass at the top; four chapels by the way, each with its brazier, on one side or the other; where the wolves come
 *   out of the snow on either side.
 * - `frostLayout` (act 2, the frozen mirrors; §4): the snow chapel's terrace. The lantern hangs fixed by the entrance,
 *   the chapel's door at the far end; five mirrors, all frozen, and six stone bases for a brazier. A frozen mirror lets
 *   the beam through straight; a thawed one turns it (a sixth to one side, to the other, or back: `MIRROR_TURN`). Two
 *   ways reach the door: the short one by mirrors 1, 3 and 5, over the cells of 2 and 4; the long one by 1, 2 and 4,
 *   over nothing. A base thaws the mirrors within two hexes of it (its circle cut in the floor): one gives 1 alone, two
 *   give 2 and 3 together (so the short way never works: 3 thawed brings 2 thawed onto it), one gives 4, one 2 and 4,
 *   one 5 and the stretcher (the only one by Tessa). With three braziers the light reaches the door only with 1, 2 and
 *   4 thawed, and the only way to have it and keep Tessa warm is the 1-base, the 2-and-4 base and the stretcher's.
 *   Every shape the layout can make is tried against every way of setting up to three braziers on six bases and every
 *   position of the thawed mirrors; the seed picks one that holds (which number each mirror has, where the bases are).
 * - `corralLayout` (act 3, the test): Piet's parents' corral, a ring of dry-stone wall with three gaps, the stretcher
 *   and three braziers inside, the family in the straw; turned a sixth or more by the seed.
 */

export const COL_RADIUS = 12;
export const FROST_RADIUS = 7;
export const CORRAL_RADIUS = 8;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;
const same = (a: Hex, b: Hex): boolean => a.q === b.q && a.r === b.r;
const add = (a: Hex, d: number, k: number): Hex => {
  const D = DIRS[((d % 6) + 6) % 6] as Hex;
  return { q: a.q + D.q * k, r: a.r + D.r * k };
};

/* ---------- the white col ---------- */

export interface ColLayout {
  radius: number;
  rocks: Hex[];
  /** The way's cells (the chapels' niches among them). */
  path: Hex[];
  /** The stretcher's way, row by row from the foot to the pass. */
  way: { x: number; y: number }[];
  /** The chapels: each one's brazier (where it stands), the way's point it is by, the side it is on (−1 west, 1 east), where the hero stands to light it. */
  chapels: { x: number; y: number; at: number; side: number; stand: { x: number; y: number } }[];
  /** Where the wolves come out of the snow, either side of the way. */
  dens: { x: number; y: number }[];
  start: { x: number; y: number };
}

export const colLayout = (seed: number): ColLayout => {
  const R = COL_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xc3a1));
  // The way's middle on each row from the foot (r = R − 1) to the pass: it winds a half hex now and then.
  const mid = new Map<number, number>();
  let x = 0;
  let dir = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let r = R - 1; r >= -(R - 1); r--) {
    mid.set(r, x);
    if (rngInt(rng, 10) < 5) {
      if (rngInt(rng, 10) < 3) dir = -dir;
      x = imax(-3000, imin(3000, x + dir * 500));
      if (x === -3000 || x === 3000) dir = -dir;
    }
  }
  const rows: number[] = [];
  for (let r = R - 1; r >= -(R - 1); r--) rows.push(r);
  const way = rows.map((r) => ({ x: mid.get(r) as number, y: r * ROW }));
  // Four chapels, one side or the other (never the same twice running), their braziers at the way's edge.
  let side = rngInt(rng, 2) === 0 ? -1 : 1;
  const chapels = [3, 8, 13, 18].map((i) => {
    const p = way[i] as { x: number; y: number };
    side = rngInt(rng, 3) === 0 ? side : -side;
    return { x: p.x + side * 1700, y: p.y, at: i, side };
  });
  const path: Hex[] = [];
  const rocks: Hex[] = [];
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    const m = mid.get(h.r);
    const foot = h.r >= R - 1;
    const mm = m ?? mid.get(R - 1) ?? 0;
    const near = Math.abs(p.x - mm) <= (foot ? 2600 : 1300);
    // A chapel's niche: two hexes off the way on its side, its row and the one above.
    const niche = chapels.some((c) => Math.abs(h.r * ROW - c.y) <= ROW && (p.x - mm) * c.side > 0 && Math.abs(p.x - c.x) <= 1300);
    if ((m !== undefined || foot) && (near || niche)) path.push(h);
    else rocks.push(h);
  }
  // Each brazier on its niche's cell nearest the chapel; the hero lights it from the cell beside it nearest the way.
  const placed = chapels.map((c) => {
    let at: Hex | null = null;
    let bd = 0x7fffffff;
    for (const h of path) {
      const p = hexToWorld(h.q, h.r);
      const d = dist2(p.x, p.y, c.x, c.y);
      if (d < bd) {
        bd = d;
        at = h;
      }
    }
    const b = at ?? { q: 0, r: 0 };
    const bw = hexToWorld(b.q, b.r);
    const wp = way[c.at] as { x: number; y: number };
    let st = bw;
    let sd = 0x7fffffff;
    for (let d = 0; d < 6; d++) {
      const n = add(b, d, 1);
      if (!path.some((x) => same(x, n))) continue;
      const p = hexToWorld(n.q, n.r);
      const dd = dist2(p.x, p.y, wp.x, wp.y);
      if (dd < sd) {
        sd = dd;
        st = p;
      }
    }
    return { x: bw.x, y: bw.y, at: c.at, side: c.side, stand: { x: st.x, y: st.y } };
  });
  const dens: { x: number; y: number }[] = [];
  for (let i = 1; i < way.length; i += 2) {
    const p = way[i] as { x: number; y: number };
    dens.push({ x: p.x + (i % 4 === 1 ? -4200 : 4200), y: p.y });
  }
  rocks.sort(sortHex);
  path.sort(sortHex);
  const foot = way[0] as { x: number; y: number };
  return { radius: R, rocks, path, way, chapels: placed, dens, start: { x: foot.x + 400, y: foot.y + 500 } };
};

/* ---------- the frozen mirrors ---------- */

export interface FrostBoard {
  lantern: { q: number; r: number; dir: number };
  door: { q: number; r: number };
  mirrors: { q: number; r: number }[];
  walls: { q: number; r: number }[];
  radius: number;
}

export interface FrostBeam {
  /** Its corners as cells: the lantern, every thawed mirror it turns at, where it stops. */
  pts: Hex[];
  cells: Hex[];
  /** The thawed mirrors it went by, in order. */
  by: number[];
  lit: boolean;
}

/** The beam from the lantern with the mirrors as `turns`, the frozen ones (`ice` 1) letting it through straight. */
export const frostTrace = (B: FrostBoard, turns: readonly number[], ice: readonly number[]): FrostBeam => {
  const out: FrostBeam = { pts: [], cells: [], by: [], lit: false };
  let q = B.lantern.q;
  let r = B.lantern.r;
  let d = B.lantern.dir;
  out.pts.push({ q, r });
  const seen = new Set<number>();
  for (let step = 0; step < 80; step++) {
    const D = DIRS[d] as Hex;
    q += D.q;
    r += D.r;
    out.cells.push({ q, r });
    if (hexLen(q, r) > B.radius || (q === B.lantern.q && r === B.lantern.r) || B.walls.some((w) => w.q === q && w.r === r)) {
      out.pts.push({ q, r });
      return out;
    }
    if (q === B.door.q && r === B.door.r) {
      out.pts.push({ q, r });
      out.lit = true;
      return out;
    }
    const m = B.mirrors.findIndex((o) => o.q === q && o.r === r);
    if (m >= 0 && !ice[m]) {
      out.pts.push({ q, r });
      out.by.push(m);
      const k = m * 8 + d;
      if (seen.has(k)) return out;
      seen.add(k);
      d = (d + (MIRROR_TURN[(turns[m] ?? 0) % 3] as number)) % 6;
    }
  }
  out.pts.push({ q, r });
  return out;
};

/** Whether some way of turning the thawed mirrors (`thawed`, a bit each) lights the door; the first such turns. */
export const frostSolve = (B: FrostBoard, thawed: number): number[] | null => {
  const n = B.mirrors.length;
  const idx: number[] = [];
  for (let i = 0; i < n; i++) if ((thawed >> i) & 1) idx.push(i);
  const ice: number[] = [];
  for (let i = 0; i < n; i++) ice.push((thawed >> i) & 1 ? 0 : 1);
  let combos = 1;
  for (let i = 0; i < idx.length; i++) combos *= 3;
  const turns: number[] = [];
  for (let i = 0; i < n; i++) turns.push(0);
  for (let c = 0; c < combos; c++) {
    let k = c;
    for (const i of idx) {
      turns[i] = k % 3;
      k = idiv(k, 3);
    }
    if (frostTrace(B, turns, ice).lit) return [...turns];
  }
  return null;
};

export interface FrostLayout extends FrostBoard {
  rocks: Hex[];
  /** The chapel's walls behind the door. */
  chapel: Hex[];
  /** The six bases, each one's mirrors (by number, within two hexes of it). */
  bases: Hex[];
  thaws: number[][];
  /** Where the stretcher rests, the braziers' pile, the hero's start; where the hero stands to turn each mirror. */
  rest: { x: number; y: number };
  pile: { x: number; y: number };
  start: { x: number; y: number };
  mirrorStand: { x: number; y: number }[];
  /** The two ways to the door, faint in the frost: their corners (cells). */
  short: Hex[];
  long: Hex[];
  /** The mirrors' first positions; the answer: the three bases (their indices) and the turns of the mirrors it uses. */
  start0: number[];
  answer: { bases: number[]; turns: number[] };
  /** Each mirror's part (its number to its role, 0 to 4: the long way's 1, 2, 4 are roles 0, 1, 3; the short's 3, 5 are 2, 4). */
  role: number[];
  /** The wolves' way in. */
  dens: { x: number; y: number }[];
}

/** The part each base plays, by the mirrors (roles) it thaws: A 1, B and D 2 and 3, C 4, E 2 and 4, F 5 and Tessa. */
const NEED = [[0], [1, 2], [3], [1, 2], [1, 3], [4]];
const RING = 2;

interface Shape {
  L: Hex;
  d0: number;
  /** By role: the long way's 1, 2, 4 are 0, 1, 3; the short way's 3 and 5 are 2 and 4. */
  m: Hex[];
  door: Hex;
  dB: number;
}

/** Every shape this layout can make (the two ways to the door), in a fixed order. */
const shapes = (): Shape[] => {
  const R = FROST_RADIUS;
  const out: Shape[] = [];
  for (let Lq = -3; Lq <= 3; Lq++) {
    const L = { q: Lq, r: R - 1 - (Lq > 0 ? Lq : 0) };
    if (hexLen(L.q, L.r) > R - 1) continue;
    for (const d0 of [1, 2])
      for (let k1 = 2; k1 <= 4; k1++) {
        const M1 = add(L, d0, k1);
        for (const t1 of [1, 5]) {
          const d1 = (d0 + t1) % 6;
          for (let a = 2; a <= 4; a++) {
            const M2 = add(M1, d1, a);
            for (const sgn of [1, -1])
              for (let x = 1; x <= 2; x++) {
                const M3 = add(M2, d1, x);
                for (let e = 1; e <= 3; e++)
                  for (let c = 1; c <= 3; c++) {
                    const dA = (d1 + sgn + 6) % 6;
                    const dB = (d1 + 2 * sgn + 6) % 6;
                    const M4 = add(M2, dA, e + x);
                    const D = add(M4, dB, c);
                    const M5 = add(M3, dA, e);
                    if (!same(add(M5, dB, x + c), D)) continue;
                    const m = [M1, M2, M3, M4, M5];
                    const all = [...m, L, D];
                    if (all.some((h, i) => all.some((g, j) => j > i && same(h, g)))) continue;
                    if (all.some((h) => hexLen(h.q, h.r) > R - 1)) continue;
                    // The door at the far end, the lantern by the entrance.
                    if (D.r > -2) continue;
                    // Room behind the door for the chapel.
                    if (hexLen(D.q + (DIRS[dB] as Hex).q, D.r + (DIRS[dB] as Hex).r) > R) continue;
                    out.push({ L, d0, m, door: D, dB });
                  }
              }
          }
        }
      }
  }
  return out;
};

/** Whether the shape holds at the mirrors' level: the door lights iff 1, 2, 4 thawed, or 1, 3, 5 with 2 and 4 frozen. */
const holds = (B: FrostBoard): boolean => {
  for (let bits = 0; bits < 32; bits++) {
    const want = (bits & 0b01011) === 0b01011 || ((bits & 0b10101) === 0b10101 && (bits & 0b01010) === 0);
    if ((frostSolve(B, bits) !== null) !== want) return false;
  }
  return true;
};

/** Every way of setting up to three braziers on the six bases (bits of the bases). */
const SETS: number[] = (() => {
  const out: number[] = [];
  for (let b = 0; b < 64; b++) {
    let n = 0;
    for (let i = 0; i < 6; i++) if ((b >> i) & 1) n++;
    if (n <= 3) out.push(b);
  }
  return out;
})();

/** The mirrors (bits) a set of bases thaws. */
export const frostThawed = (thaws: readonly (readonly number[])[], bases: number): number => {
  let out = 0;
  thaws.forEach((t, i) => {
    if ((bases >> i) & 1) for (const m of t) out |= 1 << m;
  });
  return out;
};

/**
 * Every set of up to three bases that lights the door, and those of them that keep Tessa warm (a base within two hexes
 * of the stretcher): the terrace holds with exactly one warm one.
 */
export const frostSets = (B: FrostBoard, thaws: readonly (readonly number[])[], warm: number): { lit: number[]; warm: number[] } => {
  const lit: number[] = [];
  const hot: number[] = [];
  for (const b of SETS) {
    if (frostSolve(B, frostThawed(thaws, b)) === null) continue;
    lit.push(b);
    if ((b >> warm) & 1) hot.push(b);
  }
  return { lit, warm: hot };
};

export const frostLayout = (seed: number): FrostLayout => {
  const R = FROST_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xc3f2));
  const all = shapes();
  // Tried in an order of the seed's until one holds (about one in four does).
  const order = all.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    const t = order[i] as number;
    order[i] = order[j] as number;
    order[j] = t;
  }
  for (const oi of order) {
    const sh = all[oi] as Shape;
    // The chapel behind the door: the cell straight behind it and the two beside that.
    const back = add(sh.door, sh.dB, 1);
    const chapel = [back, add(back, sh.dB + 1, 1), add(back, sh.dB - 1, 1), add(sh.door, sh.dB + 2, 1), add(sh.door, sh.dB - 2, 1)].filter((h) => hexLen(h.q, h.r) <= R);
    const B0: FrostBoard = { lantern: { ...sh.L, dir: sh.d0 }, door: sh.door, mirrors: sh.m, walls: chapel, radius: R };
    if (!holds(B0)) continue;
    // The ways' cells (no base on them: a brazier on the beam's line would read as in its way).
    const longT = frostSolve(B0, 0b01011) as number[];
    const shortT = frostSolve(B0, 0b10101) as number[];
    const longB = frostTrace(B0, longT, [0, 0, 1, 0, 1]);
    const shortB = frostTrace(B0, shortT, [0, 1, 0, 1, 0]);
    const beam = [...longB.cells, ...shortB.cells];
    const blocked = (h: Hex): boolean => hexLen(h.q, h.r) > R - 1 || same(h, sh.L) || same(h, sh.door) || sh.m.some((x) => same(x, h)) || chapel.some((x) => same(x, h)) || beam.some((x) => same(x, h));
    const cand: Hex[][] = NEED.map(() => []);
    for (const h of allCells(R)) {
      if (blocked(h)) continue;
      const got: number[] = [];
      sh.m.forEach((x, i) => {
        if (hexDist(h, x) <= RING) got.push(i);
      });
      NEED.forEach((n, k) => {
        if (got.length === n.length && n.every((v, j) => got[j] === v)) (cand[k] as Hex[]).push(h);
      });
    }
    if (cand.some((c) => !c.length)) continue;
    // The bases by the seed, apart from each other; the stretcher within two of F, farther from the others.
    let picked: Hex[] | null = null;
    let rest: Hex | null = null;
    for (let tries = 0; tries < 24 && !picked; tries++) {
      const p: Hex[] = [];
      let ok = true;
      for (let k = 0; k < 6 && ok; k++) {
        const c = (cand[k] as Hex[]).filter((h) => p.every((o) => hexDist(o, h) >= 2));
        if (!c.length) ok = false;
        else p.push(c[rngInt(rng, c.length)] as Hex);
      }
      if (!ok) continue;
      const F = p[5] as Hex;
      let best: Hex | null = null;
      let bd = 0x7fffffff;
      for (const h of allCells(R - 1)) {
        if (blocked(h) || p.some((o) => same(o, h)) || hexDist(h, F) > 2 || p.some((o, k) => k < 5 && hexDist(o, h) <= 2)) continue;
        // (Near the lantern: the stretcher came in that way.)
        const d = hexDist(h, sh.L) * 4 + hexDist(h, F);
        if (d < bd) {
          bd = d;
          best = h;
        }
      }
      if (best) {
        picked = p;
        rest = best;
      }
    }
    if (!picked || !rest) continue;
    const warm = 5;
    const sets = frostSets(B0, NEED, warm);
    // (Bits of the bases A..F: A-E-F is 0b110001.)
    if (sets.warm.length !== 1 || sets.warm[0] !== 0b110001) continue;
    // The numbers carved on the mirrors, by the seed: role → number.
    const num = [0, 1, 2, 3, 4];
    for (let i = 4; i > 0; i--) {
      const j = rngInt(rng, i + 1);
      const t = num[i] as number;
      num[i] = num[j] as number;
      num[j] = t;
    }
    const mirrors: Hex[] = [];
    const role: number[] = [];
    for (let n = 0; n < 5; n++) {
      const ro = num.indexOf(n);
      role.push(ro);
      mirrors.push({ ...(sh.m[ro] as Hex) });
    }
    // The bases in a fixed order on the board (top first), so the marks read the same way every time.
    const order6 = [0, 1, 2, 3, 4, 5].sort((a, b) => sortHex(picked[a] as Hex, picked[b] as Hex));
    const bases = order6.map((k) => ({ ...(picked[k] as Hex) }));
    const thaws = order6.map((k) => (NEED[k] as number[]).map((ro) => num[ro] as number).sort((a, b) => a - b));
    const ans = [0, 4, 5].map((k) => order6.indexOf(k)).sort((a, b) => a - b);
    const turns = mirrors.map((_, n) => longT[role[n] as number] as number);
    const rocks = [{ ...sh.L }, ...mirrors.map((m) => ({ ...m })), ...chapel.map((h) => ({ ...h }))].sort(sortHex);
    const rw = hexToWorld(rest.q, rest.r);
    // Where to stand by a mirror: the open cell next to it nearest the stretcher.
    const stand = (m: Hex): { x: number; y: number } => {
      let best: { x: number; y: number } | null = null;
      let bd = 0x7fffffff;
      for (let d = 0; d < 6; d++) {
        const n = add(m, d, 1);
        if (hexLen(n.q, n.r) > R - 1 || same(n, sh.L) || same(n, sh.door) || rocks.some((x) => same(x, n))) continue;
        const w = hexToWorld(n.q, n.r);
        const dd = dist2(w.x, w.y, rw.x, rw.y);
        if (dd < bd) {
          bd = dd;
          best = w;
        }
      }
      return best ?? hexToWorld(m.q, m.r + 1);
    };
    // The pile: an open cell by the stretcher, toward the lantern.
    let pile = rw;
    let pd = 0x7fffffff;
    for (let d = 0; d < 6; d++) {
      const n = add(rest, d, 1);
      if (blocked(n) || bases.some((b) => same(b, n))) continue;
      const w = hexToWorld(n.q, n.r);
      const L0 = hexToWorld(sh.L.q, sh.L.r);
      const dd = dist2(w.x, w.y, L0.x, L0.y);
      if (dd < pd) {
        pd = dd;
        pile = w;
      }
    }
    // The mirrors start anywhere but the answer's.
    const start0 = mirrors.map(() => rngInt(rng, 3));
    if (start0.every((t, i) => t === turns[i])) start0[0] = ((start0[0] as number) + 1) % 3;
    const dens = [
      { x: -R * 1000 - 800, y: 0 },
      { x: R * 1000 + 800, y: -ROW * 2 },
    ];
    return {
      lantern: { ...sh.L, dir: sh.d0 },
      door: { ...sh.door },
      mirrors,
      walls: chapel.map((h) => ({ ...h })).sort(sortHex),
      radius: R,
      rocks,
      chapel: chapel.map((h) => ({ ...h })).sort(sortHex),
      bases,
      thaws,
      rest: rw,
      pile: { x: pile.x, y: pile.y },
      start: { x: rw.x + 900, y: rw.y - 500 },
      mirrorStand: mirrors.map(stand),
      short: shortB.pts.map((h) => ({ ...h })),
      long: longB.pts.map((h) => ({ ...h })),
      start0,
      answer: { bases: ans, turns },
      role,
      dens,
    };
  }
  throw new Error('frostLayout: no terrace holds');
};

/* ---------- the corral ---------- */

export interface CorralLayout {
  radius: number;
  rocks: Hex[];
  /** The wall's stones (rocks) and its gaps (cells). */
  wall: Hex[];
  gaps: Hex[];
  /** Where the stretcher rests, the braziers stand, the family sleeps, the sheep lie (drawn only). */
  rest: { x: number; y: number };
  braziers: { x: number; y: number }[];
  family: { x: number; y: number }[];
  sheep: { x: number; y: number }[];
  /** Outside each gap: where the wolves come in from; the storm's sky the watcher circles in. */
  dens: { x: number; y: number }[];
  sky: { x: number; y: number }[];
  start: { x: number; y: number };
  /** The corral's turn (sixths). */
  turn: number;
}

/** A cell turned `k` sixths about the middle. */
const turnHex = (h: Hex, k: number): Hex => {
  let q = h.q;
  let r = h.r;
  for (let i = 0; i < ((k % 6) + 6) % 6; i++) {
    const nq = -r;
    const nr = q + r;
    q = nq;
    r = nr;
  }
  return { q, r };
};

export const corralLayout = (seed: number): CorralLayout => {
  const R = CORRAL_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xc3c7));
  const turn = rngInt(rng, 6);
  const at = (q: number, r: number): { x: number; y: number } => {
    const h = turnHex({ q, r }, turn);
    return hexToWorld(h.q, h.r);
  };
  // The wall: the ring at five; three gaps, two cells each, a third of the ring apart.
  const ring = allCells(5).filter((h) => hexLen(h.q, h.r) === 5);
  const gapAt = [0, 2, 4].map((d) => turnHex(add({ q: 0, r: 0 }, d, 5), turn));
  const gaps: Hex[] = [];
  for (const g of gapAt) {
    gaps.push(g);
    // (Its neighbour on the ring, clockwise.)
    const n = ring.find((h) => hexDist(h, g) === 1 && !gaps.some((x) => same(x, h)) && !gapAt.some((x) => same(x, h)));
    if (n) gaps.push(n);
  }
  const wall = ring.filter((h) => !gaps.some((g) => same(g, h)));
  const dens = gapAt.map((g) => {
    const w = hexToWorld(g.q, g.r);
    return { x: idiv(w.x * 14, 10), y: idiv(w.y * 14, 10) };
  });
  const sky: { x: number; y: number }[] = [];
  for (let d = 0; d < 6; d++) {
    const h = add({ q: 0, r: 0 }, d, 7);
    sky.push(hexToWorld(h.q, h.r));
  }
  return {
    radius: R,
    rocks: [...wall].sort(sortHex),
    wall: [...wall].sort(sortHex),
    gaps: gaps.sort(sortHex),
    rest: at(1, 0),
    braziers: [at(2, -2), at(-2, 2), at(-1, -2)],
    family: [at(-3, 1), at(-2, 0), at(-3, 2)],
    sheep: [at(0, 3), at(1, 2), at(-1, 3), at(3, -3), at(2, 1), at(0, -3)],
    dens,
    sky,
    start: at(2, 0),
    turn,
  };
};
